import React, { createContext, useContext, useState } from 'react';
import {
  Badge,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
const DEFAULT_COLLAPSED_WIDTH = 76;

interface SidebarContextValue {
  /** When true, the desktop rail shows icons only. */
  collapsed: boolean;
  /** Which drawer instance is rendering (mobile temporary vs desktop permanent). */
  surface: 'mobile' | 'desktop';
}

const SidebarContext = createContext<SidebarContextValue>({
  collapsed: false,
  surface: 'desktop',
});

/** Read the sidebar's current collapsed state from any sub-component. */
const useSidebarContext = (): SidebarContextValue => useContext(SidebarContext);

/** Which drawer surface is rendering this subtree. */
export const useSidebarSurface = (): 'mobile' | 'desktop' =>
  useSidebarContext().surface;

export interface SidebarProps {
  /** Expanded drawer width in pixels. */
  width: number;
  /** Rail width in pixels when collapsed to icons (desktop only). */
  collapsedWidth?: number;
  /** Collapse the desktop rail to icons only. */
  collapsed?: boolean;
  /** Controls the temporary (mobile) drawer visibility. */
  mobileOpen: boolean;
  /** Invoked when the temporary drawer requests to close. */
  onMobileClose: () => void;
  /** Composable drawer content (header, sections, navigation…). */
  children: React.ReactNode;
}

/**
 * Responsive, themeable sidebar shell.
 *
 * Renders a temporary drawer on mobile (always expanded) and a permanent,
 * collapsible rail on desktop. The collapsed state is exposed to descendants
 * through context, so the `Sidebar.*` sub-components adapt automatically.
 */
const SidebarRoot: React.FC<SidebarProps> = ({
  width,
  collapsedWidth = DEFAULT_COLLAPSED_WIDTH,
  collapsed = false,
  mobileOpen,
  onMobileClose,
  children,
}) => {
  const theme = useTheme();
  const desktopWidth = collapsed ? collapsedWidth : width;

  const widthTransition = theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  const paperBaseSx = {
    boxSizing: 'border-box' as const,
    bgcolor: theme.palette.background.paper,
    // No divider line — the rail blends seamlessly into the content edge.
    borderRight: 'none',
    overflowX: 'hidden' as const,
  };

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: desktopWidth },
        flexShrink: { sm: 0 },
        transition: { sm: widthTransition },
      }}
    >
      {/* Mobile: temporary drawer is always rendered expanded. */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { ...paperBaseSx, width },
        }}
      >
        <SidebarContext.Provider value={{ collapsed: false, surface: 'mobile' }}>
          {children}
        </SidebarContext.Provider>
      </Drawer>

      {/* Desktop: permanent, collapsible rail. */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            ...paperBaseSx,
            width: desktopWidth,
            transition: widthTransition,
          },
        }}
      >
        <SidebarContext.Provider value={{ collapsed, surface: 'desktop' }}>
          {children}
        </SidebarContext.Provider>
      </Drawer>
    </Box>
  );
};

export interface SidebarHeaderProps {
  /** Brand content shown when the sidebar is expanded. */
  children: React.ReactNode;
  /** Optional compact brand content shown when collapsed to icons. */
  collapsedChildren?: React.ReactNode;
}

/** Branded header slot, aligned with the AppBar height. */
const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  children,
  collapsedChildren,
}) => {
  const { collapsed } = useSidebarContext();

  return (
    <Toolbar
      sx={{
        px: 2,
        py: 3,
        overflow: 'hidden',
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}
    >
      {collapsed ? collapsedChildren ?? children : children}
    </Toolbar>
  );
};

/** Renders its children only while the sidebar is expanded. */
const SidebarExpandedOnly: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { collapsed } = useSidebarContext();
  return collapsed ? null : <>{children}</>;
};

/** Navigation list container. */
const SidebarNav: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <List component="nav" disablePadding sx={{ py: 1.25 }}>
    {children}
  </List>
);

export interface SidebarNavGroupProps {
  label: string;
  /** Whether the group starts expanded (only relevant when not collapsed). */
  defaultOpen?: boolean;
  children: React.ReactNode;
}

/**
 * A collapsible group of nav items. When the rail is collapsed to icons the
 * group label/toggle is hidden and the items render directly as icons.
 */
const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
  label,
  defaultOpen = true,
  children,
}) => {
  const theme = useTheme();
  const { collapsed } = useSidebarContext();
  const [open, setOpen] = useState(defaultOpen);

  if (collapsed) {
    return <Box sx={{ '& + &': { mt: 1 } }}>{children}</Box>;
  }

  return (
    <Box sx={{ mb: 1, '&:first-of-type': { mt: 0 }, mt: 1.5 }}>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        disableRipple
        sx={{
          mx: 1.5,
          px: 1,
          py: 0.5,
          mb: 0.5,
          borderRadius: '10px',
          '&:hover': { background: 'transparent' },
        }}
      >
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            variant: 'overline',
            sx: {
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.1em',
              color: theme.palette.text.secondary,
            },
          }}
        />
        {open ? (
          <ExpandLessIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
        ) : (
          <ExpandMoreIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {children}
        </List>
      </Collapse>
    </Box>
  );
};

export interface SidebarNavItemProps {
  icon: React.ReactNode;
  label: string;
  selected?: boolean;
  id?: string;
  /** Attention badge (e.g. incomplete setup). Dot when true; number/string when provided. */
  badge?: boolean | number | string;
  badgeTooltip?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  locked?: boolean;
}

/**
 * A single navigation entry with the themed selected state. When the rail is
 * collapsed the label is hidden and a tooltip exposes it on hover.
 */
const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  icon,
  label,
  selected = false,
  id,
  badge,
  badgeTooltip,
  onClick,
  locked = false,
}) => {
  const theme = useTheme();
  const { collapsed } = useSidebarContext();
  const showBadge = badge === true || (typeof badge === 'number' && badge > 0) || (typeof badge === 'string' && badge.length > 0);
  const useDot = badge === true;
  const tooltipTitle = badgeTooltip ? `${label} — ${badgeTooltip}` : label;

  const iconNode = showBadge && collapsed ? (
    <Badge
      color="warning"
      variant={useDot ? 'dot' : 'standard'}
      badgeContent={useDot ? undefined : badge}
      overlap="circular"
      sx={{
        '& .MuiBadge-badge': {
          border: `2px solid ${theme.palette.background.paper}`,
          ...(useDot ? { width: 10, height: 10, borderRadius: '50%' } : null),
        },
      }}
    >
      {icon}
    </Badge>
  ) : (
    icon
  );

  const button = (
    <ListItemButton
      id={id}
      selected={selected}
      onClick={onClick}
      aria-disabled={locked || undefined}
      sx={{
        minHeight: 46,
        borderRadius: '12px',
        color: locked ? theme.palette.text.disabled : theme.palette.text.secondary,
        opacity: locked ? 0.72 : 1,
        transition: theme.transitions.create(
          ['background-color', 'color', 'box-shadow'],
          { duration: theme.transitions.duration.shorter },
        ),
        ...(collapsed
          ? {
              width: 46,
              height: 46,
              p: 0,
              mx: 'auto',
              justifyContent: 'center',
            }
          : { px: 1.75, py: 1, justifyContent: 'flex-start' }),
        '& .MuiListItemIcon-root': {
          minWidth: collapsed ? 0 : 38,
          color: 'inherit',
          justifyContent: 'center',
          transition: 'inherit',
          '& .MuiSvgIcon-root': { fontSize: 22 },
        },
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main}14`,
          color: theme.palette.primary.main,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          boxShadow: `0 6px 16px ${theme.palette.primary.main}3D`,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
          },
          '& .MuiListItemIcon-root': {
            color: theme.palette.primary.contrastText,
          },
        },
      }}
    >
      <ListItemIcon>{iconNode}</ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            noWrap: true,
            sx: { fontSize: '0.9rem', fontWeight: selected ? 700 : 500 },
          }}
        />
      )}
      {!collapsed && showBadge && (
        <Badge
          color="warning"
          variant={useDot ? 'dot' : 'standard'}
          badgeContent={useDot ? undefined : badge}
          sx={{ ml: 1, flexShrink: 0 }}
        />
      )}
      {!collapsed && locked && (
        <LockOutlinedIcon aria-hidden="true" sx={{ ml: 1, fontSize: 18 }} />
      )}
    </ListItemButton>
  );

  return (
    <ListItem
      disablePadding
      sx={{ display: 'block', px: collapsed ? 0 : 1.5, mb: 0.5 }}
    >
      {collapsed ? (
        <Tooltip title={locked ? `${label} — Pilot Mode` : tooltipTitle} placement="right">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
};

export interface SidebarCollapseToggleProps {
  onClick: () => void;
  /** Accessible/tooltip labels (localized by the caller). */
  expandLabel?: string;
  collapseLabel?: string;
}

/** Desktop-only control that toggles the icon rail. */
const SidebarCollapseToggle: React.FC<SidebarCollapseToggleProps> = ({
  onClick,
  expandLabel = 'Expand menu',
  collapseLabel = 'Collapse menu',
}) => {
  const theme = useTheme();
  const { collapsed } = useSidebarContext();
  const label = collapsed ? expandLabel : collapseLabel;

  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'flex' },
        justifyContent: collapsed ? 'center' : 'flex-end',
        px: 1.5,
        py: 1,
      }}
    >
      <Tooltip title={label} placement="right">
        <IconButton
          onClick={onClick}
          size="small"
          aria-label={label}
          sx={{
            color: theme.palette.text.secondary,
            borderRadius: '10px',
            transition: theme.transitions.create(['background-color', 'color'], {
              duration: theme.transitions.duration.shorter,
            }),
            '&:hover': {
              backgroundColor: `${theme.palette.primary.main}14`,
              color: theme.palette.primary.main,
            },
          }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

/**
 * Composable sidebar. Use the root as the responsive shell and the attached
 * sub-components to assemble its content (Header, ExpandedOnly, Nav, NavGroup,
 * NavItem, CollapseToggle).
 */
export const Sidebar = Object.assign(SidebarRoot, {
  Header: SidebarHeader,
  ExpandedOnly: SidebarExpandedOnly,
  Nav: SidebarNav,
  NavGroup: SidebarNavGroup,
  NavItem: SidebarNavItem,
  CollapseToggle: SidebarCollapseToggle,
});

export default Sidebar;
